import React, { useState, useEffect, useRef, useCallback } from "react";
import { Editor } from "react-draft-wysiwyg";
import {
  EditorState,
  ContentState,
  convertToRaw,
  AtomicBlockUtils,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { MediaSelectorModal } from "../../../../shared/components/modals";
import { BlogService } from "../../../../services/blogService";
import { BlogCategoryDto } from "../../../../types/app/blogTypes";
import { BLOG_SITE_URL } from "../../../../services/paths";
import { LoadingItem } from "../../../../shared/components/loading";

interface BlogPost {
  id?: string;
  slug?: string;
  title?: string;
  excerpt?: string;
  content?: string;
  featuredImage?: string;
  category?: string;
  categories?: string[];
  tags?: string[];
  author?: {
    name: string;
    avatar: string;
  };
  publishDate?: string;
  readTime?: string;
  metaTitle?: string;
  metaDescription?: string;
  views?: number;
  featured?: boolean;
  status?: "draft" | "published" | "trashed";
}

interface PostEditorProps {
  post: Partial<BlogPost>;
  onSave: (post: Partial<BlogPost>, isDraft: boolean) => void;
  onCancel: () => void;
  onChange?: () => void;
  isNew: boolean;
}

// Resizable Image Component for inline images
interface ResizableImageProps {
  src: string;
  height: string;
  width: string;
  onHeightChange: (height: string) => void;
  onDelete?: () => void;
}

const ResizableImage: React.FC<ResizableImageProps> = ({
  src,
  height,
  width,
  onHeightChange,
  onDelete,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [currentHeight, setCurrentHeight] = useState<number>(
    parseInt(height) || 300,
  );
  const [displayHeight, setDisplayHeight] = useState<number>(currentHeight);
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      startYRef.current = e.clientY;
      startHeightRef.current = currentHeight;
    },
    [currentHeight],
  );

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startYRef.current;
      const newHeight = Math.max(50, startHeightRef.current + deltaY);
      setDisplayHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setCurrentHeight(displayHeight);
      onHeightChange(`${displayHeight}px`);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, displayHeight, onHeightChange]);

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <div
      ref={containerRef}
      className="resizable-image-container"
      style={{
        position: "relative",
        display: "inline-block",
        width: width || "100%",
        maxWidth: "100%",
      }}
    >
      <img
        src={src}
        alt=""
        style={{
          width: "100%",
          height: `${isResizing ? displayHeight : currentHeight}px`,
          objectFit: "cover",
          display: "block",
        }}
      />
      {/* Delete Button */}
      <div
        className="delete-button"
        onClick={handleDelete}
        title="Remove image"
        style={{
          position: "absolute",
          top: "8px",
          right: "8px",
          width: "28px",
          height: "28px",
          background: "rgba(255, 255, 255, 0.95)",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "opacity 0.2s ease",
          zIndex: 10,
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 72 72"
        >
          <path
            fill="#FA5252"
            d="M 32.5 9 C 28.364 9 25 12.364 25 16.5 L 25 18 L 17 18 C 14.791 18 13 19.791 13 22 C 13 24.209 14.791 26 17 26 L 17.232422 26 L 18.671875 51.916016 C 18.923875 56.449016 22.67875 60 27.21875 60 L 44.78125 60 C 49.32125 60 53.076125 56.449016 53.328125 51.916016 L 54.767578 26 L 55 26 C 57.209 26 59 24.209 59 22 C 59 19.791 57.209 18 55 18 L 47 18 L 47 16.5 C 47 12.364 43.636 9 39.5 9 L 32.5 9 z M 32.5 16 L 39.5 16 C 39.775 16 40 16.224 40 16.5 L 40 18 L 32 18 L 32 16.5 C 32 16.224 32.225 16 32.5 16 z M 36 28 C 37.104 28 38 28.896 38 30 L 38 47.923828 C 38 49.028828 37.104 49.923828 36 49.923828 C 34.896 49.923828 34 49.027828 34 47.923828 L 34 30 C 34 28.896 34.896 28 36 28 z M 27.392578 28.001953 C 28.459578 27.979953 29.421937 28.827641 29.460938 29.931641 L 30.085938 47.931641 C 30.123938 49.035641 29.258297 49.959047 28.154297 49.998047 C 28.131297 49.999047 28.108937 50 28.085938 50 C 27.012938 50 26.125891 49.148359 26.087891 48.068359 L 25.462891 30.068359 C 25.424891 28.964359 26.288578 28.040953 27.392578 28.001953 z M 44.607422 28.001953 C 45.711422 28.039953 46.575109 28.964359 46.537109 30.068359 L 45.912109 48.068359 C 45.874109 49.148359 44.986063 50 43.914062 50 C 43.891062 50 43.868703 49.999047 43.845703 49.998047 C 42.741703 49.960047 41.876063 49.035641 41.914062 47.931641 L 42.539062 29.931641 C 42.577062 28.827641 43.518422 27.979953 44.607422 28.001953 z"
          />
        </svg>
      </div>
      {/* Resize Handle */}
      <div
        className="resize-handle"
        onMouseDown={handleMouseDown}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "12px",
          background:
            "linear-gradient(to bottom, transparent, rgba(0,0,0,0.1))",
          cursor: "ns-resize",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "4px",
            background: "rgba(0,0,0,0.3)",
            borderRadius: "2px",
          }}
        />
      </div>
      {/* Height Display Tooltip */}
      {isResizing && (
        <div
          style={{
            position: "absolute",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.8)",
            color: "white",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: "bold",
            zIndex: 1000,
          }}
        >
          {displayHeight}px
        </div>
      )}
    </div>
  );
};

// Media Component for custom block rendering
const MediaComponent = (props: any) => {
  const entity = props.contentState.getEntity(props.block.getEntityAt(0));
  const { src, height = "300", width = "100%" } = entity.getData();
  const type = entity.getType();
  const entityKey = props.block.getEntityAt(0);

  const handleHeightChange = (newHeight: string) => {
    // Call the update function passed through blockProps
    if (props.blockProps?.onUpdateHeight) {
      props.blockProps.onUpdateHeight(entityKey, newHeight);
    }
  };

  const handleDelete = () => {
    // Call the delete function passed through blockProps
    if (props.blockProps?.onDeleteBlock) {
      props.blockProps.onDeleteBlock(props.block.getKey());
    }
  };

  if (type === "IMAGE") {
    return (
      <ResizableImage
        src={src}
        height={height}
        width={width}
        onHeightChange={handleHeightChange}
        onDelete={handleDelete}
      />
    );
  }
  return null;
};

// Helper function to create block renderer with update and delete callbacks
const createBlockRendererFn = (
  onUpdateHeight: (entityKey: string, height: string) => void,
  onDeleteBlock: (blockKey: string) => void,
) => {
  return (block: any) => {
    if (block.getType() === "atomic") {
      return {
        component: MediaComponent,
        editable: false,
        props: {
          onUpdateHeight,
          onDeleteBlock,
        },
      };
    }
    return null;
  };
};

const PostEditor: React.FC<PostEditorProps> = ({
  post,
  onSave,
  onCancel,
  onChange,
  isNew,
}) => {
  const [title, setTitle] = useState(post.title || "");
  const [slug, setSlug] = useState(post.slug || "");
  const [excerpt, setExcerpt] = useState(post.excerpt || "");
  const [readTime, setReadTime] = useState(
    post.readTime ? post.readTime.replace(" min read", "") : "",
  );
  const [editorState, setEditorState] = useState(() => {
    if (post.content) {
      const contentBlock = htmlToDraft(post.content);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(
          contentBlock.contentBlocks,
        );
        return EditorState.createWithContent(contentState);
      }
    }
    return EditorState.createEmpty();
  });

  // Ref to access latest editorState without triggering callback recreation
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;
  const [category, setCategory] = useState(post.category || "");
  const [categories, setCategories] = useState<string[]>(
    post.categories || (post.category ? [post.category] : []),
  );
  const [tags, setTags] = useState(
    post.tags?.map((tag, index) => ({ id: index.toString(), text: tag })) || [],
  );
  const [featuredImage, setFeaturedImage] = useState(post.featuredImage || "");
  const [showPermalink, setShowPermalink] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showInlineImageModal, setShowInlineImageModal] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<
    BlogCategoryDto[]
  >([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const validateForm = () => {
    const errors: Record<string, string> = {};

    // Validate title
    if (!title || title.trim() === "") {
      errors.title = "Title is required";
    }

    // Validate content
    const contentHtml = draftToHtml(
      convertToRaw(editorState.getCurrentContent()),
    );
    const stripped = contentHtml.replace(/<[^>]*>/g, "").trim();
    if (!stripped || stripped.length === 0) {
      errors.content = "Content is required";
    }

    // Validate categories
    if (!categories || categories.length === 0) {
      errors.categories = "Error: Select a blog category";
    }

    // Validate featured image
    if (!featuredImage || featuredImage.trim() === "") {
      errors.featuredImage = "Error: Select a featured image";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const data = await BlogService.getBlogCategories(false);
        setAvailableCategories(Array.isArray(data) ? data : []);
      } catch (error) {
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (post && !isNew) {
      setTitle(post.title || "");
      setSlug(post.slug || "");
      setExcerpt(post.excerpt || "");
      setReadTime(post.readTime ? post.readTime.replace(" min read", "") : "");

      const categoryIdValue = (post as any).categoryId || "";
      setCategory(categoryIdValue);
      setCategories(categoryIdValue ? [categoryIdValue] : []);

      setFeaturedImage(post.featuredImage || "");
      setTags(
        post.tags?.map((tag, index) => ({ id: index.toString(), text: tag })) ||
          [],
      );

      if (post.content) {
        const contentBlock = htmlToDraft(post.content);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(
            contentBlock.contentBlocks,
          );
          setEditorState(EditorState.createWithContent(contentState));
        }
      }
    }
  }, [post, isNew]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted && isNew && title && !slug) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setSlug(generatedSlug);
    }

    return () => {
      isMounted = false;
    };
  }, [title, isNew, slug]);

  const clearFieldError = (fieldName: string) => {
    if (validationErrors[fieldName]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    clearFieldError("title");
    onChange?.(); // Track changes
  };

  const handleEditorStateChange = (state: any) => {
    setEditorState(state);
    clearFieldError("content");
    onChange?.(); // Track changes
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteTag = (i: number) => {
    setTags(tags.filter((tag, index) => index !== i));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAddTag = (tag: { id: string; text: string }) => {
    setTags([...tags, tag]);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDragTag = (
    tag: { id: string; text: string },
    currPos: number,
    newPos: number,
  ) => {
    const newTags = tags.slice();
    newTags.splice(currPos, 1);
    newTags.splice(newPos, 0, tag);
    setTags(newTags);
  };

  const handleCategoryChange = (categoryValue: string, isChecked: boolean) => {
    if (isChecked) {
      setCategories([categoryValue]);
      setCategory(categoryValue);
    } else {
      setCategories([]);
      setCategory("");
    }
    clearFieldError("categories");
    onChange?.(); // Track changes
  };

  const handleSave = (isDraft: boolean) => {
    // Validate form before saving
    if (!validateForm()) {
      return; // Stop if validation fails
    }

    // Custom transform for IMAGE entities to include all attributes
    const customEntityTransform = (entity: any, text: string) => {
      if (entity.type === "IMAGE") {
        const { src, height, width, style, fetchpriority, decoding } =
          entity.data;
        const dataAttrs = entity.data["data-nimg"]
          ? ` data-nimg="${entity.data["data-nimg"]}"`
          : "";
        const fetchAttr = fetchpriority
          ? ` fetchpriority="${fetchpriority}"`
          : "";
        const decodingAttr = decoding ? ` decoding="${decoding}"` : "";
        const styleValue = `height: ${height || "auto"}; width: ${
          width || "100%"
        }; ${style || ""}`.trim();

        return `<img src="${src}" alt="" style="${styleValue}"${fetchAttr}${decodingAttr}${dataAttrs}/>`;
      }
      return undefined; // Let draftToHtml handle other entities
    };

    const contentHtml = draftToHtml(
      convertToRaw(editorState.getCurrentContent()),
      undefined, // hashtagConfig
      undefined, // directional
      customEntityTransform,
    );

    const updatedPost: Partial<BlogPost> = {
      ...post,
      title,
      slug,
      excerpt,
      content: contentHtml,
      readTime: readTime ? `${readTime} min read` : "0 min read",
      category: category,
      categories: categories,
      tags: tags.map((tag) => tag.text),
      featuredImage,
      status: isDraft ? "draft" : "published",
    };

    onSave(updatedPost, isDraft);
  };

  const handleImageUpload = () => {
    setShowImageModal(true);
  };

  const handleImageConfirm = (imageUrl: string) => {
    setFeaturedImage(imageUrl);
    clearFieldError("featuredImage");
    onChange?.(); // Track changes
  };

  const handleInlineImageConfirm = (imageUrl: string) => {
    if (imageUrl) {
      // Insert image into editor at cursor position with default height
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity(
        "IMAGE",
        "IMMUTABLE",
        {
          src: imageUrl,
          height: "300px",
          width: "100%",
          style: "object-fit: cover;",
          fetchpriority: "high",
        },
      );
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newEditorState = EditorState.set(editorState, {
        currentContent: contentStateWithEntity,
      });
      setEditorState(
        AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, " "),
      );
    }
    setShowInlineImageModal(false);
  };

  // Handle image height update from ResizableImage component
  const handleUpdateHeight = useCallback(
    (entityKey: string, newHeight: string) => {
      const currentEditorState = editorStateRef.current;
      const contentState = currentEditorState.getCurrentContent();
      const entity = contentState.getEntity(entityKey);
      const data = entity.getData();

      // Create new content state with updated entity data
      const newContentState = contentState.mergeEntityData(entityKey, {
        ...data,
        height: newHeight,
      });

      const newEditorState = EditorState.push(
        currentEditorState,
        newContentState,
        "apply-entity",
      );

      setEditorState(newEditorState);
    },
    [], // Empty deps - uses ref to access latest state
  );

  // Handle deleting an atomic block (image)
  const handleDeleteBlock = useCallback((blockKey: string) => {
    const currentEditorState = editorStateRef.current;
    const contentState = currentEditorState.getCurrentContent();
    const blockMap = contentState.getBlockMap();

    // Remove the block with the given key
    const newBlockMap = blockMap.delete(blockKey);
    const newContentState = contentState.merge({
      blockMap: newBlockMap,
    }) as any;

    const newEditorState = EditorState.push(
      currentEditorState,
      newContentState,
      "remove-range",
    );

    setEditorState(newEditorState);
  }, []);

  // Create block renderer with height update and delete callbacks
  const blockRenderer = useCallback(
    (block: any) =>
      createBlockRendererFn(handleUpdateHeight, handleDeleteBlock)(block),
    [handleUpdateHeight, handleDeleteBlock],
  );

  // Custom image button component
  const ImageButton = () => (
    <div className="rdw-embedded-wrapper">
      <div
        className="rdw-option-wrapper"
        onClick={() => setShowInlineImageModal(true)}
        title="Insert Image from Gallery"
      >
        <img
          src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUiIGhlaWdodD0iMTQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0iIzAwMCIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJNMTQuNzQxIDBILjI2Qy4xMTYgMCAwIC4xMzYgMCAuMzA0djEzLjM5MmMwIC4xNjguMTE2LjMwNC4yNTkuMzA0SDE0Ljc0Yy4xNDMgMCAuMjU5LS4xMzYuMjU5LS4zMDRWLjMwNEMxNSAuMTM2IDE0Ljg4NCAwIDE0Ljc0MSAwem0tLjI1OCAxMy4zOTFILjUxN1YuNjFoMTMuOTY2VjEzLjM5eiIvPjxwYXRoIGQ9Ik00LjEzOCA2LjczOGMuNzk0IDAgMS40NC0uNzYgMS40NC0xLjY5NXMtLjY0Ni0xLjY5NS0xLjQ0LTEuNjk1Yy0uNzk0IDAtMS40NC43Ni0xLjQ0IDEuNjk1IDAgLjkzNC42NDYgMS42OTUgMS40NCAxLjY5NXptMC0yLjc4MWMuNTA5IDAgLjkyMy40ODcuOTIzIDEuMDg2IDAgLjU5OC0uNDE0IDEuMDg2LS45MjMgMS4wODYtLjUwOSAwLS45MjMtLjQ4Ny0uOTIzLTEuMDg2IDAtLjU5OS40MTQtMS4wODYuOTIzLTEuMDg2ek0xLjgxIDEyLjE3NGMuMDYgMCAuMTIyLS4wMjUuMTcxLS4wNzZMNi4yIDcuNzI4bDIuNjY0IDMuMTM0YS4yMzIuMjMyIDAgMCAwIC4zNjYgMCAuMzQzLjM0MyAwIDAgMCAwLS40M0w3Ljk4NyA4Ljk2OWwyLjM3NC0zLjA2IDIuOTEyIDMuMTQyYy4xMDYuMTEzLjI3LjEwNS4zNjYtLjAyYS4zNDMuMzQzIDAgMCAwLS4wMTYtLjQzbC0zLjEwNC0zLjM0N2EuMjQ0LjI0NCAwIDAgMC0uMTg2LS4wOC4yNDUuMjQ1IDAgMCAwLS4xOC4xTDcuNjIyIDguNTM3IDYuMzk0IDcuMDk0YS4yMzIuMjMyIDAgMCAwLS4zNTQtLjAxM2wtNC40IDQuNTZhLjM0My4zNDMgMCAwIDAtLjAyNC40My4yNDMuMjQzIDAgMCAwIC4xOTQuMTAzeiIvPjwvZz48L3N2Zz4="
          alt=""
          style={{ width: "15px", height: "14px" }}
        />
      </div>
    </div>
  );

  return (
    <div className="post-editor">
      <div className="editor-header">
        <button className="btn-back" onClick={onCancel}>
          ← Back to Posts
        </button>
      </div>

      <div className="editor-container">
        <div className="editor-main">
          <div className="title-section">
            <div className="field-group">
              <input
                type="text"
                className={`post-title-input ${
                  validationErrors.title ? "error" : ""
                }`}
                placeholder="Add title"
                value={title}
                onChange={handleTitleChange}
              />
              {validationErrors.title && (
                <span className="field-error">{validationErrors.title}</span>
              )}
            </div>

            <div className="permalink-section">
              <input
                type="text"
                className="permalink-url-input"
                value={`${BLOG_SITE_URL}/${slug}`}
                disabled={!showPermalink}
                onChange={(e) => {
                  const fullUrl = e.target.value;
                  const newSlug = fullUrl.replace(`${BLOG_SITE_URL}/`, "");
                  setSlug(newSlug);
                }}
              />
              {!showPermalink && (
                <button
                  className="btn-edit-permalink"
                  onClick={() => setShowPermalink(true)}
                >
                  Update
                </button>
              )}
              {showPermalink && (
                <button
                  className="btn-ok"
                  onClick={() => setShowPermalink(false)}
                >
                  OK
                </button>
              )}
            </div>

            <div className="field-group">
              <textarea
                className="post-excerpt-input"
                placeholder="Add a brief excerpt or summary (optional)"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                rows={1}
              />
            </div>

            <div className="field-group">
              <label className="field-label readtime-label">
                Read Time (minutes)
              </label>
              <input
                type="number"
                className="post-readtime-input"
                placeholder="5"
                value={readTime}
                onChange={(e) => setReadTime(e.target.value)}
                min="0"
              />
            </div>
          </div>

          {validationErrors.content && (
            <span className="field-error">{validationErrors.content}</span>
          )}
          <div
            className={`editor-wrapper ${
              validationErrors.content ? "error" : ""
            }`}
          >
            <Editor
              editorState={editorState}
              onEditorStateChange={handleEditorStateChange}
              wrapperClassName="editor-wrapper-class"
              editorClassName="editor-class"
              toolbarClassName="editor-toolbar"
              toolbar={{
                options: [
                  "inline",
                  "blockType",
                  "fontSize",
                  "list",
                  "textAlign",
                  "colorPicker",
                  "link",
                  "embedded",
                  "image",
                  "history",
                ],
                inline: {
                  options: ["bold", "italic", "underline"],
                },
                blockType: {
                  inDropdown: true,
                  options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6"],
                },
                list: {
                  options: ["unordered", "ordered"],
                },
                link: {
                  inDropdown: false,
                  showOpenOptionOnHover: true,
                  defaultTargetOption: "_blank",
                  options: ["link", "unlink"],
                },
                embedded: {
                  defaultSize: {
                    height: "315",
                    width: "560",
                  },
                },
                colorPicker: {
                  colors: [
                    "rgb(97,189,109)",
                    "rgb(26,188,156)",
                    "rgb(84,172,210)",
                    "rgb(44,130,201)",
                    "rgb(147,101,184)",
                    "rgb(71,85,119)",
                    "rgb(204,204,204)",
                    "rgb(65,168,95)",
                    "rgb(0,168,133)",
                    "rgb(61,142,185)",
                    "rgb(41,105,176)",
                    "rgb(85,57,130)",
                    "rgb(40,50,78)",
                    "rgb(0,0,0)",
                    "rgb(247,218,100)",
                    "rgb(251,160,38)",
                    "rgb(235,107,86)",
                    "rgb(226,80,65)",
                    "rgb(163,143,132)",
                    "rgb(239,239,239)",
                    "rgb(255,255,255)",
                    "rgb(250,197,28)",
                    "rgb(243,121,52)",
                    "rgb(209,72,65)",
                    "rgb(184,49,47)",
                    "rgb(124,112,107)",
                    "rgb(209,213,216)",
                  ],
                },
                image: {
                  component: ImageButton,
                  popupClassName: "custom-image-popup",
                },
              }}
              customBlockRenderFunc={blockRenderer}
            />
          </div>
        </div>

        <div className="editor-sidebar">
          <div className="sidebar-section publish-section">
            <h3>Publish</h3>
            <div className="publish-actions">
              <button
                className="btn-save-draft"
                onClick={() => handleSave(true)}
              >
                Save Draft
              </button>
              <button className="btn-publish" onClick={() => handleSave(false)}>
                {isNew ? "Publish" : "Update"}
              </button>
            </div>
            {!isNew && (
              <div className="trash-link-container">
                <button className="btn-trash-link">Move to Trash</button>
              </div>
            )}
          </div>

          <div
            className={`sidebar-section ${
              validationErrors.categories ? "error" : ""
            }`}
          >
            <h3>Categories *</h3>
            {validationErrors.categories && (
              <span className="field-error">{validationErrors.categories}</span>
            )}
            <div
              className={`categories-list ${
                validationErrors.categories ? "error" : ""
              }`}
            >
              {isLoadingCategories ? (
                <div style={{ padding: "10px", color: "#666" }}>
                  <LoadingItem />
                </div>
              ) : availableCategories.length === 0 ? (
                <div style={{ padding: "10px", color: "#666" }}>
                  No categories available.{" "}
                  <a
                    href="/app/blog/categories"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#007bff", textDecoration: "none" }}
                  >
                    Create one
                  </a>
                </div>
              ) : (
                availableCategories.map((cat) => (
                  <label key={cat.id} className="category-checkbox">
                    <input
                      type="checkbox"
                      value={cat.id}
                      checked={categories.includes(cat.id)}
                      onChange={(e) =>
                        handleCategoryChange(cat.id, e.target.checked)
                      }
                    />
                    {cat.name}
                  </label>
                ))
              )}
            </div>
          </div>

          <div
            className={`sidebar-section ${
              validationErrors.featuredImage ? "error" : ""
            }`}
          >
            <h3>Featured Image *</h3>
            {validationErrors.featuredImage && (
              <span className="field-error">
                {validationErrors.featuredImage}
              </span>
            )}
            {featuredImage ? (
              <div className="featured-image-preview">
                <img src={featuredImage} alt="Featured" />
                <button
                  className="btn-remove-image"
                  onClick={() => setFeaturedImage("")}
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                className={`btn-set-image ${
                  validationErrors.featuredImage ? "error" : ""
                }`}
                onClick={handleImageUpload}
              >
                {/* <FiImage />  */}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.0013 6.66667C6.73768 6.66667 7.33464 6.06971 7.33464 5.33333C7.33464 4.59695 6.73768 4 6.0013 4C5.26492 4 4.66797 4.59695 4.66797 5.33333C4.66797 6.06971 5.26492 6.66667 6.0013 6.66667Z"
                    stroke="#292D32"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8.66536 1.33203H5.9987C2.66536 1.33203 1.33203 2.66536 1.33203 5.9987V9.9987C1.33203 13.332 2.66536 14.6654 5.9987 14.6654H9.9987C13.332 14.6654 14.6654 13.332 14.6654 9.9987V6.66536"
                    stroke="#292D32"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M10.5 3.33203H14.1667"
                    stroke="#292D32"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                  <path
                    d="M12.332 5.16667V1.5"
                    stroke="#292D32"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                  <path
                    d="M1.78125 12.635L5.06792 10.4283C5.59458 10.075 6.35458 10.115 6.82792 10.5217L7.04792 10.715C7.56792 11.1617 8.40792 11.1617 8.92792 10.715L11.7012 8.335C12.2212 7.88833 13.0612 7.88833 13.5812 8.335L14.6679 9.26833"
                    stroke="#292D32"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                Set featured image
              </button>
            )}
          </div>
        </div>
      </div>

      <MediaSelectorModal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        onSelect={handleImageConfirm}
        title="Select Featured Image"
        currentImage={featuredImage}
      />

      <MediaSelectorModal
        show={showInlineImageModal}
        onHide={() => {
          setShowInlineImageModal(false);
          if ((window as any).__imageUploadResolve) {
            (window as any).__imageUploadResolve({ data: { link: "" } });
            delete (window as any).__imageUploadResolve;
          }
        }}
        onSelect={handleInlineImageConfirm}
        title="Insert Image"
        currentImage=""
      />
    </div>
  );
};

export default PostEditor;
